<template>
  <div
    ref="candidateFormEvents"
    class="candidate-form-events"
  >
    <h5>
      Events
      <a
        href="#"
        class="btn btn-sm btn-xs btn-success float-end"
        @click.prevent="newEvent()"
      >Add to event</a>
    </h5>
    <div
      v-if="!showEvents"
      class="alert alert-sm alert-info"
      role="alert"
    >
      Candidate has no events yet
    </div>
    <table
      v-show="showEvents"
      class="table"
    >
      <tbody>
        <tr
          v-for="(cevent, index) in events"
          :key="cevent.uid"
          :class="fEventTrClass(cevent)"
        >
          <td
            v-if="showErrors"
            class="tight text-danger error"
          >
            <span
              v-if="showEventErrors(cevent)"
              class="material-icons error"
              data-bs-toggle="tooltip"
              :data-bs-title="eventErrorMessage(cevent)"
            />
          </td>
          <td class="event">
            <span class="text-overflow-dynamic-container">
              <span
                class="text-overflow-dynamic-ellipsis"
                :title="cevent.eventName"
              >
                {{ cevent.eventName }}
              </span>
            </span>
            <input
              type="hidden"
              :name="`candidate[candidate_events_attributes][${index}][id]`"
              :value="cevent.id"
            >
            <input
              type="hidden"
              :name="`candidate[candidate_events_attributes][${index}][_cancel]`"
              :value="fEventCanceled(cevent)"
            >
            <input
              type="hidden"
              :name="`candidate[candidate_events_attributes][${index}][cancellation_reason_id]`"
              :value="cevent.cancellationReasonId"
            >
            <input
              type="hidden"
              :name="`candidate[candidate_events_attributes][${index}][cancellation_reason]`"
              :value="cevent.cancellationReason"
            >
            <input
              type="hidden"
              :name="`candidate[candidate_events_attributes][${index}][event_id]`"
              :value="cevent.eventId"
            >
            <input
              v-model="cevent.priority"
              type="hidden"
              :name="`candidate[candidate_events_attributes][${index}][priority_str]`"
            >
            <input
              v-model="cevent.languageId"
              type="hidden"
              :name="`candidate[candidate_events_attributes][${index}][event_language_id]`"
            >
          </td>
          <td class="tight">
            <a
              href="#"
              class="btn btn-sm btn-xs btn-success"
              @click.prevent="editEvent(cevent)"
            >
              {{ cevent.editable ? 'Edit' : 'View' }}
            </a>
          </td>
        </tr>
      </tbody>
    </table>
    <teleport
      to="#vue-portal-target"
      append
    >
      <candidate-event-modal
        ref="candidateEventModal"
        :modal-title="eventModalTitle"
        :autocomplete-url="eventsUrl"
        :cancellation-reasons="cancellationReasons"
        @update="updateEvent"
        @cancel="cancelEvent"
      />
    </teleport>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, useTemplateRef } from 'vue'
import $ from 'jquery'
import { Tooltip } from 'bootstrap'
import CandidateEventModal from './candidate-event-modal.vue'
import { EventCandidateStatus } from './../event-candidate-status'
import { initializeFormFields } from '../site-utils'
import type { CancellationReason } from './types'

interface CandidateEvent {
  editable: boolean;
  statusId: number;
  eventId: number | string | null;
  id: number | null;
  eventName: string;
  uid: string;
  priority: string | null;
  languageId: number | null;
  errors?: string[];
  canEdit: boolean;
  canDelete: boolean;
  canCancel: boolean;
  cancellationReasonId: number | string | null;
  cancellationReason: string | null;
}

defineProps({
  eventsUrl: { default: null, type: String },
  cancellationReasons: { type: Array as () => CancellationReason[], default: (): CancellationReason[] => [] }
})

const candidateFormEvents = useTemplateRef<HTMLElement>('candidateFormEvents')
const candidateEventModal = useTemplateRef<InstanceType<typeof CandidateEventModal>>('candidateEventModal')
const newEventRef = useTemplateRef<HTMLSelectElement>('newEvent')

const events = ref<CandidateEvent[]>([])
const eventModalTitle = ref('')
const eventModalChangeEvent = ref(false)

let errorTooltips: Tooltip | null = null

const showErrors = computed((): boolean => {
  let hasErrors = false
  for (const event of events.value) {
    if (event.errors && event.errors.length > 0) {
      hasErrors = true
      break
    }
  }
  return hasErrors
})

const showEvents = computed((): boolean => {
  return events.value.length > 0
})

onMounted(() => {
  nextTick(() => {
    if (candidateFormEvents.value) {
      initializeFormFields(candidateFormEvents.value)
      errorTooltips = new Tooltip(candidateFormEvents.value, {
        selector: '[data-bs-toggle="tooltip"]',
        title: (el) => el.getAttribute('data-bs-title') ?? '',
      })
    }
  })
})

onBeforeUnmount(() => {
  candidateFormEvents.value?.querySelectorAll('[data-bs-toggle="tooltip"]').forEach((el) => {
    Tooltip.getInstance(el)?.dispose()
  })
  errorTooltips?.dispose()
  errorTooltips = null
})

const setEvents = (newEvents: CandidateEvent[] | null | undefined): void => {
  if (typeof newEvents !== 'undefined' && newEvents != null) {
    events.value = newEvents
  } else {
    events.value = []
  }
}

const setAddToEvent = (addToEvent: {name: string; id: string} | null): void => {
  if (addToEvent) {
    const newOption = new Option(addToEvent.name, addToEvent.id, false, false)
    if (newEventRef.value) {
      $(newEventRef.value).append(newOption).trigger('change')
    }
  }
}

const newEvent = (): void => {
  eventModalTitle.value = 'Add candidate to event'
  eventModalChangeEvent.value = true

  candidateEventModal.value?.showModal(null)
}

const editEvent = (cevent: CandidateEvent): void => {
  const event = JSON.parse(JSON.stringify(cevent)) as CandidateEvent
  eventModalTitle.value = 'Edit candidate event'
  eventModalChangeEvent.value = false
  candidateEventModal.value?.showModal(event)
}

const showEventErrors = (cevent: CandidateEvent): boolean => {
  return (cevent.errors && cevent.errors.length > 0) || false
}

const eventErrorMessage = (cevent: CandidateEvent): string | null => {
  if (cevent.errors && cevent.errors.length > 0) {
    return cevent.errors.join('; ')
  } else {
    return null
  }
}

const updateEvent = (newCandidateEvent: unknown): void => {
  let event = newCandidateEvent as CandidateEvent
  let isSuccess = true
  let pushEvent = true

  event = JSON.parse(JSON.stringify(event)) as CandidateEvent
  // detect if event exists
  for (let i = 0; i < events.value.length; i++) {
    if (events.value[i].eventId === event.eventId) {
      pushEvent = false
      if (events.value[i].editable) {
        events.value.splice(i, 1, event)
      } else {
        isSuccess = false
        candidateEventModal.value?.setErrorMessage('Candidate has already been added to event')
      }
    }
  }

  if (isSuccess) {
    candidateEventModal.value?.hideModal()
    if (pushEvent) {
      if (event.statusId === EventCandidateStatus.new) {
        event.canDelete = true
        event.canCancel = false
      }
      events.value.push(event)
    }
  }
}

const cancelEvent = (event: unknown, cancellationReasonId?: string, cancellationReason?: string): void => {
  const candidateEvent = event as CandidateEvent
  candidateEventModal.value?.hideModal()
  for (const cevent of events.value) {
    if (candidateEvent.eventId === cevent.eventId) {
      cevent.statusId = EventCandidateStatus.canceled
      cevent.cancellationReasonId = cancellationReasonId ?? null
      cevent.cancellationReason = cancellationReason ?? null
      break
    }
  }
}

const fEventTrClass = (cevent: CandidateEvent): Record<string, boolean> => {
  return { 'deleted': cevent.statusId == EventCandidateStatus.deleted, 'canceled': cevent.statusId == EventCandidateStatus.canceled }
}

const fEventCanceled = (cevent: CandidateEvent): string => {
  return cevent.statusId == EventCandidateStatus.deleted || cevent.statusId === EventCandidateStatus.canceled ? '1' : '0'
}

defineExpose({
  setAddToEvent,
  setEvents,
})
</script>
